import { useEffect, type ReactNode } from "react"
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
  useNavigate,
} from "@tanstack/react-router"
import type { QueryClient } from "@tanstack/react-query"
import { PostHogProvider } from "@posthog/react"
import appCss from "@/styles.css?url"
import { Toaster } from "sonner"
import { posthog } from "@/core/posthog"
import { ThemeProvider, themeInitScript } from "@/lib/theme"
import { PostHogTracker } from "@/lib/posthog"
import {
  desktopRendererReady,
  onDesktopNavigate,
  validateDesktopRoute,
} from "@/lib/desktop"

// Root route. Holds the SSR document shell and the per-request QueryClient in
// router context (consumed by loaders for prefetch + dehydration).
export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1, viewport-fit=cover",
      },
      { name: "color-scheme", content: "light dark" },
      { title: "Attend Console" },
    ],
    links: [{ rel: "stylesheet", href: appCss }],
  }),
  component: RootComponent,
})

function RootComponent() {
  return (
    <RootDocument>
      <PostHogProvider client={posthog}>
        <ThemeProvider>
          <PostHogTracker />
          <DesktopNavigationBridge />
          <Outlet />
          <Toaster position="top-center" richColors closeButton />
        </ThemeProvider>
      </PostHogProvider>
    </RootDocument>
  )
}

/**
 * One app-lifetime subscriber, above auth and event routes. Installing it at
 * the root means a cold notification/protocol link can enter a protected route
 * first, be sent through the normal sign-in guard, and resume via `next`.
 */
function DesktopNavigationBridge() {
  const navigate = useNavigate()

  useEffect(() => {
    const unsubscribe = onDesktopNavigate((candidate) => {
      const route = validateDesktopRoute(candidate)
      if (!route) return
      // The route is runtime data, so it cannot be part of TanStack's generated
      // literal union. validateDesktopRoute has constrained it to real console
      // route shapes before this single type-boundary assertion.
      void navigate({ to: route } as Parameters<typeof navigate>[0])
    })
    desktopRendererReady()
    return unsubscribe
  }, [navigate])

  return null
}

function RootDocument({ children }: { children: ReactNode }) {
  // The inline theme script below intentionally adds/removes `.dark` before
  // React hydrates. The server cannot know the browser's stored/OS theme, so
  // that single root attribute difference is expected.
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        {/* Set the .dark class before first paint to avoid a theme flash. */}
        <script dangerouslySetInnerHTML={{ __html: themeInitScript }} />
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  )
}
